import { useEffect, useState } from "react";
import api from "../services/api";
import {
  Card,
  Spin,
  Tag,
  Row,
  Col,
  Select,
  message,
  Drawer,
  Descriptions,
  Image,
  Divider,
  Tabs,
  Table,
  Collapse,
  InputNumber,
  Button,
  Checkbox,
} from "antd";

import { PlayCircleOutlined } from "@ant-design/icons";

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeesAssignments, setEmployeesAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));
  const [givenQuantities, setGivenQuantities] = useState({});
  const [fullFilledItems, setFullFilledItems] = useState({});
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [completedCategories, setCompletedCategories] = useState({});
  const isToday = (date) => {
    const today = new Date().toISOString().split("T")[0];
    return date.startsWith(today);
  };

  const updateGivenQty = (orderItemId, qty) => {
    setGivenQuantities((prev) => ({
      ...prev,
      [orderItemId]: qty,
    }));
  };

  const toggleFullFill = (item, checked) => {
    setFullFilledItems((prev) => ({
      ...prev,
      [item.id]: checked,
    }));

    updateGivenQty(item.id, checked ? item.quantity : 0);
  };

  const groupByCategory = (items) => {
    const grouped = {};

    items.forEach((item) => {
      const category = item.product?.subcategory?.name || "Others";

      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(item);
    });

    return grouped;
  };

  const [activeTab, setActiveTab] = useState("today");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const filteredOrders = selectedCustomer
    ? orders.filter((o) => o.customer?.id === selectedCustomer)
    : orders;

  const todayOrders = filteredOrders.filter((o) => isToday(o.createdAt));

  const pastOrders = filteredOrders.filter((o) => !isToday(o.createdAt));

  useEffect(() => {
    loadOrders();

    if (user?.role === "admin" || user?.role === "manager") {
      loadEmployees();
      loadEmployeesAssignment();

      if (!drawerOpen) {
        const interval = setInterval(() => {
          loadOrders();
          loadEmployeesAssignment();
        }, 30000);

        return () => clearInterval(interval);
      }
    }
  }, [drawerOpen, user?.role]);

  const getColumns = () => {
    const base = [
      {
        title: "Order No",
        dataIndex: "orderNumber",
        render: (_, record) => (
          <a onClick={() => openOrder(record)}>#{record.orderNumber}</a>
        ),
      },

      {
        title: "Delivery Type",
        render: (r) => r.deliveryType,
      },

      {
        title: "Total",
        render: (r) => "£" + r.totalAmount,
      },

      {
        title: "Status",

        render: (_, record) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Tag color={getStatusColor(record.status)}>{record.status}</Tag>
          </div>
        ),
      },
    ];

    // Employee & customer
    if (user.role === "employee" || user.role === "customer") {
      return [
        ...base,

        {
          title: "Customer",
          render: (r) => r.customer?.name,
        },
        {
          title: "Shop Name",
          render: (r) => r.customer?.shop_name,
        },
        {
          title: "Shop Code",
          render: (r) => r.customer?.shop_code,
        },
      ];
    }

    // Admin / Manager
    if (user.role === "admin" || user.role === "manager") {
      return [
        ...base,

        {
          title: "Customer",
          render: (r) => r.customer?.name,
        },

        {
          title: "Shop Name",
          render: (r) => r.customer?.shop_name,
        },
        {
          title: "Shop Code",
          render: (r) => r.customer?.shop_code,
        },

        {
          title: "Assigned To",

          render: (record) => (
            <Select
              style={{ width: 180 }}
              value={
                record.assignedEmployee?.id
                  ? Number(record.assignedEmployee.id)
                  : undefined
              }
              disabled={
                record.status === "in-progress" || record.status === "completed"
              }
              onChange={(val) => assignOrder(record.id, val)}
              options={employees.map((e) => ({
                value: Number(e.id),
                label: e.name,
              }))}
            />
          ),
        },
      ];
    }

    return base;
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      let res = await api.get("/orders/my");
      if (user.role === "employee") {
        res = res.data?.filter((o) => o.assignedEmployee?.id === user.id);
        setOrders(res || []);
      } else {
        setOrders(res.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await api.get("/admin/users?role=employee");
      setEmployees(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadEmployeesAssignment = async () => {
    try {
      const res = await api.get("/orders/employee-stats");
      setEmployeesAssignments(res.data?.employees || []);
    } catch (err) {
      console.error(err);
    }
  };

  const updateOrderStatus = async (status, orderData = null) => {
    try {
      const items = Object.entries(givenQuantities).map(
        ([orderItemId, qty]) => ({
          orderItemId: Number(orderItemId),
          givenQuantity: Number(qty || 0),
        }),
      );

      await api.put(`/orders/${(orderData || selectedOrder).id}/status`, {
        status,
        items,
      });

      message.success("Order updated successfully");

      setDrawerOpen(false);

      loadOrders();
    } catch (err) {
      console.error(err);

      message.error("Failed to update order");
    }
  };

  const assignOrder = async (orderId, employeeId) => {
    try {
      await api.put(`/orders/${orderId}/assign`, {
        employeeId,
      });

      message.success("Order assigned successfully");
      loadOrders();
    } catch (err) {
      message.error("Assignment failed");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "orange";

      case "assigned":
        return "blue";

      case "in-progress":
        return "purple";

      case "completed":
        return "green";

      case "cancelled":
        return "red";

      default:
        return "default";
    }
  };
  const openOrder = (order) => {
    setSelectedOrder(order);
    setDrawerOpen(true);
  };

  const markCategoryDone = (category) => {
    setCompletedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  if (loading) return <Spin />;

  const customerOptions = [
    ...new Map(
      orders
        .filter((o) => o.customer)
        .map((o) => [
          o.customer.id,
          {
            value: o.customer.id,
            label: `${o.customer.name}`,
          },
        ]),
    ).values(),
  ];

  return (
    <>
      {/* Employee Daily WorkLoad */}
      {(user?.role === "admin" || user?.role === "manager") &&
        employeesAssignments.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ marginBottom: 16 }}>Employee Daily WorkLoad</h2>

            <div
              style={{
                display: "flex",
                gap: 16,
                overflowX: "auto",
                paddingBottom: 10,
              }}
            >
              {employeesAssignments.map((emp) => (
                <Card
                  key={emp.id}
                  style={{
                    minWidth: 260,
                    borderRadius: 12,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    {/* Name */}
                    <div>
                      <strong>Name:</strong>
                      <div>{emp.name}</div>
                    </div>

                    {/* Active Orders */}
                    <div>
                      <strong>Active Orders:</strong>
                      <div>
                        <Tag color="blue">{emp.activeOrders || 0}</Tag>
                      </div>
                    </div>

                    {/* Completed Today */}
                    <div>
                      <strong>Completed Today:</strong>
                      <div>
                        <Tag color="green">{emp.completedToday || 0}</Tag>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
      {(user?.role === "employee" ||
        user?.role === "manager" ||
        user?.role === "admin") && (
        <div style={{ marginBottom: 16 }}>
          <Select
            allowClear
            showSearch
            placeholder="Filter by customer"
            style={{ width: 320 }}
            value={selectedCustomer}
            onChange={setSelectedCustomer}
            options={customerOptions}
            optionFilterProp="label"
          />
        </div>
      )}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "today",

            label: "Today",

            children: (
              <Table
                rowKey="id"
                dataSource={todayOrders}
                columns={getColumns()}
              />
            ),
          },

          {
            key: "past",

            label: "Past Orders",

            children: (
              <Table
                rowKey="id"
                dataSource={pastOrders}
                columns={getColumns()}
              />
            ),
          },
        ]}
      />
      <Drawer
        title={`#${selectedOrder?.orderNumber}`}
        width={800}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        {selectedOrder &&
          (() => {
            const grouped = groupByCategory(selectedOrder.OrderItems);
            const orderSummary = selectedOrder.OrderItems.reduce(
              (acc, item) => {
                const rate = Number(item.price || 0);
                const orderedQty = Number(item.quantity || 0);
                const givenQty = Number(
                  givenQuantities[item.id] ?? item.givenQuantity ?? 0,
                );

                acc.orderedQty += orderedQty;
                acc.givenQty += givenQty;
                acc.orderedAmount += orderedQty * rate;
                acc.givenAmount += givenQty * rate;

                return acc;
              },
              {
                orderedQty: 0,
                givenQty: 0,
                orderedAmount: 0,
                givenAmount: 0,
              },
            );
            const collapseItems = Object.entries(grouped).map(
              ([category, items], index) => ({
                key: String(index),
                label: (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        color: completedCategories[category] ? "#fff" : "#000",
                      }}
                    >
                      {category}
                    </span>

                    {user?.role === "employee" &&
                      selectedOrder.status !== "completed" && (
                        <Button
                          type={
                            completedCategories[category]
                              ? "default"
                              : "primary"
                          }
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            markCategoryDone(category);
                          }}
                        >
                          {completedCategories[category] ? "Undo" : "Done"}
                        </Button>
                      )}
                  </div>
                ),
                styles: {
                  header: {
                    background: completedCategories[category]
                      ? "#52c41a"
                      : "#fafafa",
                    transition: "all .3s",
                  },
                },

                children: (
                  <Table
                    rowKey="id"
                    pagination={false}
                    dataSource={items}
                    size="small"
                    rowClassName={(record) => {
                      const orderedQty = Number(record.quantity || 0);

                      const givenQty = Number(
                        givenQuantities[record.id] ?? record.givenQuantity ?? 0,
                      );

                      const hasBeenProcessed =
                        givenQuantities[record.id] !== undefined ||
                        fullFilledItems[record.id] !== undefined ||
                        selectedOrder?.status === "completed";

                      return hasBeenProcessed && orderedQty !== givenQty
                        ? "quantity-mismatch-row"
                        : "";
                    }}
                    columns={[
                      {
                        title: "Product",

                        render: (_, item) => {
                          const product = item.product;

                          const color = product?.attributes?.find(
                            (a) =>
                              a.Attribute?.name?.toLowerCase() === "colors",
                          )?.AttributeValue?.value;

                          return (
                            <div>
                              <div>
                                <div
                                  style={{
                                    fontWeight: 600,
                                    color: "#1677ff",
                                    cursor: product?.images?.length
                                      ? "pointer"
                                      : "default",
                                  }}
                                  onClick={() => {
                                    if (product?.images?.length) {
                                      setPreviewImage(
                                        `${process.env.REACT_APP_API_URL}${product.images[0].image_url}`,
                                      );

                                      setPreviewVisible(true);
                                    }
                                  }}
                                >
                                  {product?.name}
                                </div>
                              </div>
                            </div>
                          );
                        },
                      },

                      {
                        title: "Qty.",
                        dataIndex: "quantity",
                        width: 100,
                        align: "center",
                      },

                      // {
                      //   title: "Amount",
                      //   width: 120,
                      //   align: "left",
                      //   render: (_, item) => {
                      //     const rate = Number(item.price || 0);
                      //     return `£${(rate * Number(item.quantity || 0)).toFixed(2)}`;
                      //   },
                      // },

                      {
                        title: "Final Amount",
                        width: 120,
                        align: "left",
                        render: (_, item) => {
                          const rate = Number(item.price || 0);
                          const givenQty = Number(
                            givenQuantities[item.id] ?? item.givenQuantity ?? 0,
                          );

                          return `£${(rate * givenQty).toFixed(2)}`;
                        },
                      },
                      {
                        title: "Given Qty.",
                        width: 220,

                        render: (_, item) => {
                          const checked =
                            fullFilledItems[item.id] ??
                            Number(item.givenQuantity) ===
                              Number(item.quantity);

                          const givenQtyvalue =
                            givenQuantities[item.id] ?? item.givenQuantity ?? 0;

                          return (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                              }}
                            >
                              {user?.role === "employee" &&
                                selectedOrder.status !== "completed" && (
                                  <Checkbox
                                    checked={checked}
                                    onChange={(e) =>
                                      toggleFullFill(item, e.target.checked)
                                    }
                                  >
                                    Full
                                  </Checkbox>
                                )}

                              <InputNumber
                                min={0}
                                max={item.quantity}
                                disabled={
                                  checked ||
                                  user?.role !== "employee" ||
                                  selectedOrder.status === "completed"
                                }
                                value={givenQtyvalue}
                                onChange={(val) => updateGivenQty(item.id, val)}
                                style={{ width: 80, height: 40 }}
                              />
                            </div>
                          );
                        },
                      },
                    ]}
                  />
                ),
              }),
            );
            return (
              <>
                {/* Start Order Icon */}

                {user?.role === "employee" &&
                  selectedOrder.status === "assigned" && (
                    <div
                      style={{
                        color: "#1677ff",
                        fontSize: 14,
                        cursor: "pointer",
                        position: "absolute",
                        right: "25px",
                        top: "15px",
                      }}
                      onClick={() => {
                        setSelectedOrder(selectedOrder);

                        updateOrderStatus("in-progress", selectedOrder);
                      }}
                    >
                      <PlayCircleOutlined
                        title="Start Order"
                        label="Start Order"
                      />
                      <span>Start Order</span>
                    </div>
                  )}
                {user?.role !== "customer" && (
                  <>
                    <Descriptions column={1} bordered>
                      <Descriptions.Item label="Customer">
                        {selectedOrder.customer?.name}
                      </Descriptions.Item>

                      <Descriptions.Item label="Status">
                        {selectedOrder.status}
                      </Descriptions.Item>

                      <Descriptions.Item label="Delivery">
                        {selectedOrder.deliveryType}
                      </Descriptions.Item>
                      <Descriptions.Item label="Notes">
                        {selectedOrder.notes}
                      </Descriptions.Item>

                      <Descriptions.Item label="Total Amount">
                        £ {selectedOrder.totalAmount}
                      </Descriptions.Item>
                    </Descriptions>

                    <Divider />
                  </>
                )}

                <Collapse items={collapseItems} defaultActiveKey={["0"]} />

                <Card style={{ marginTop: 16 }}>
                  <Row gutter={16}>
                    <Col span={6}>
                      <strong>Ordered Qty</strong>
                      <div>{orderSummary.orderedQty}</div>
                    </Col>

                    <Col span={6}>
                      <strong>Given Qty</strong>
                      <div>{orderSummary.givenQty}</div>
                    </Col>

                    <Col span={6}>
                      <strong>Ordered Amount</strong>
                      <div>£{orderSummary.orderedAmount.toFixed(2)}</div>
                    </Col>

                    <Col span={6}>
                      <strong>Final Amount</strong>
                      <div>£{orderSummary.givenAmount.toFixed(2)}</div>
                    </Col>
                  </Row>
                </Card>
                {user?.role === "employee" &&
                  selectedOrder.status !== "completed" && (
                    <div
                      style={{
                        marginTop: 20,
                        display: "flex",
                        gap: 12,
                      }}
                    >
                      {/* <Button
                        style={{
                          background: "#f59e0b",
                          borderColor: "#f59e0b",
                          color: "#fff",
                        }}
                        onClick={() => updateOrderStatus("paused")}
                      >
                        Pause Order
                      </Button> */}
                      <Button
                        type="primary"
                        style={{
                          background: "#16a34a",
                          borderColor: "#16a34a",
                        }}
                        onClick={() => updateOrderStatus("completed")}
                      >
                        Complete Order
                      </Button>
                    </div>
                  )}
              </>
            );
          })()}
        <Image
          style={{ display: "none" }}
          preview={{
            visible: previewVisible,
            src: previewImage,
            onVisibleChange: (v) => setPreviewVisible(v),
          }}
        />
      </Drawer>
    </>
  );
}
